<template>
  <v-card>
    <v-card-title>
      {{ $t('payment.milestone.import') }}
      <v-spacer/>
      <v-btn
        icon
        small
        @click="handleClose"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-subtitle>
      <span v-html="$t('payment.milestone.importGuide', { fileLink: '/MilestonesTemplate.xlsx' })"></span>
    </v-card-subtitle>
    <v-card-text>
      <v-file-input
        outlined
        dense
        :label="$t('payment.milestone.file')"
        v-model="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        @change="handleFileChange"
      />
      <ErrorsListAlert :items="errorMessage" />
      <ErrorsListAlert :items="warningMessages" type="warning" />
      <v-alert
        v-if="notAllSelectedWarning"
        type="info"
        outlined
        dense
      >
        {{notAllSelectedWarning}}
      </v-alert>
      <v-data-table
        v-model="selectedMilestones"
        :headers="headers"
        :items="milestonesWithError"
        :single-expand="false"
        show-expand
        show-select
        dense
        item-key="index"
        :item-class="importedMilestonesClasses"
      >
        <template v-slot:no-data>
          {{ $t('payment.milestone.importPreview') }}
        </template>
        <template v-slot:header.data-table-select="{}">
          <v-simple-checkbox
            :value="selectAll"
            :indeterminate="selectAllIndeterminate"
            :ripple="false"
            @input="handleSelectAll"
          />
        </template>
        <template v-slot:item.data-table-select="{ item, isSelected }">
          <v-simple-checkbox
            :value="isSelected"
            @input="(v) => handleSelectMilestone(v, item)"
            :indeterminate="isSelected && selectedSubMilestones[item.index] && selectedSubMilestones[item.index].length !== item.subMilestones.length"
            :disabled="item.hasError"
            :ripple="false"
          />
        </template>
        <template v-slot:item.name="{ item }">
          <v-chip small color="orange">{{item.name}}</v-chip>
        </template>
        <template v-slot:item.description="{ item }">
          <small>{{item.description}}</small>
        </template>
        <template v-slot:item.comment="{ item }">
          <small>{{item.comment}}</small>
        </template>
        <template v-slot:item.purchaseOrder="{ item }">
          <small class="d-block"><b class="mr-1">{{ $t('part.orderNumber') }}</b>{{item.purchaseOrder}}</small>
          <small class="d-block"><b class="mr-1">{{ $t(partTranslationKey + '.name') }}</b>{{item.part.name}}</small>
        </template>
        <template v-slot:item.date="{ item }">
          <small class="d-block">
            <b>{{ $t('payment.milestone.originalDate') }}</b>
            <span class="nowrap ml-1">{{item.originalDate | formatDate}}</span>
          </small>
          <small class="d-block">
            <b>{{ $t('payment.milestone.forecastedDate') }}</b>
            <span class="nowrap ml-1">{{item.forecastedDate | formatDate}}</span>
          </small>
        </template>
        <template v-slot:item.percent="{ item }">
          {{item.percentOfContractPrice | toFixed}}%
        </template>
        <template v-slot:item.paymentAmountCents="{ item }">
          <span class="nowrap">{{item.paymentAmountCents | formatCurrency}}</span>
        </template>
        <template  v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="subpayments-table">
            <v-data-table
              v-model="selectedSubMilestones[item.index]"
              :headers="headers"
              :items="item.subMilestones"
              hide-default-footer
              hide-default-header
              disable-pagination
              show-select
              dense
              item-key="index"
              :item-class="importedMilestonesClasses"
              @item-selected="(v) => handleSelectSubMilestone(item, v)"
            >
              <template v-slot:item.data-table-select="{ item, isSelected, select }">
                <v-simple-checkbox
                  :value="isSelected"
                  @input="select"
                  :disabled="item.hasError"
                  :ripple="false"
                />
              </template>
              <template v-slot:item.name="{ item }">
                <v-chip small color="orange">{{item.name}}</v-chip>
              </template>
              <template v-slot:item.description="{ item }">
                <small>{{item.description}}</small>
              </template>
              <template v-slot:item.comment="{ item }">
                <small>{{item.comment}}</small>
              </template>
              <template v-slot:item.purchaseOrder="{ item }">
                <small class="d-block"><b class="mr-1">{{ $t('part.orderNumber') }}</b>{{item.purchaseOrder}}</small>
                <small class="d-block"><b class="mr-1">{{ $t(partTranslationKey + '.name') }}</b>{{item.part.name}}</small>
              </template>
              <template v-slot:item.percent="{ item }">
                <small class="d-block ">
                  {{item.percentOfContractPrice | toFixed}}% <b>{{ $t('payment.milestone.ofContract') }}</b>
                </small>
                <small class="d-block nowrap">
                  {{item.percentOfMilestone | toFixed}}% <b>{{ $t('payment.milestone.ofMilestone') }}</b>
                </small>
              </template>
              <template v-slot:item.date="{ item }">
                <small class="d-block">
                  <b>{{ $t('payment.milestone.originalDate') }}</b>
                  <span class="nowrap ml-1">{{item.originalDate | formatDate}}</span>
                </small>
                <small class="d-block">
                  <b>{{ $t('payment.milestone.forecastedDate') }}</b>
                  <span class="nowrap ml-1">{{item.forecastedDate | formatDate}}</span>
                </small>
              </template>
              <template v-slot:item.paymentAmountCents="{ item }">
                <span class="nowrap">{{item.paymentAmountCents | formatCurrency}}</span>
              </template>
            </v-data-table>
          </td>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        rounded
        small
        :color="notAllSelectedWarning ? 'warning' : 'primary'"
        @click="handleImportMilestones"
        :disabled="!selectedMilestones.length || sumOfSelectedMilestonesHigherThanContractPrice"
      >
        {{ $t('payment.milestone.import') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import XLSX from 'xlsx'
import { DEFAULT_CONTRACT, EXCEL_LABELS, ISO_DATE_FORMAT, ACCEPTED_EXCEL_IMPORT_DATE_FORMATS } from '@/helpers'
import dayjs from 'dayjs'
import { addPaymentMilestone, saveSubMilestones, updatePaymentMilestone, checkMilestonesNamesDuplicates } from '@/services'
import { milestonesImportModel, errorMessageGenerator } from '@/validators'
import { getLocalizationContext } from '@/helpers/translations'

export default {
  name: 'MilestonesImport',
  props: {
    contract: {
      type: Object,
      default: () => DEFAULT_CONTRACT
    },
    milestones: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      headers: [
        { text: this.$t('payment.milestone.name'), value: 'name' },
        { text: this.$t('common.description'), value: 'description' },
        { text: this.$t('payment.milestone.forecastedDate'), value: 'date' },
        { text: this.$t('payment.milestone.percentOfContract'), value: 'percent' },
        { text: this.$t('payment.milestone.payment'), value: 'paymentAmountCents' },
        { text: this.$t('payment.milestone.orderNumber'), value: 'purchaseOrder' },
        { text: this.$t('payment.milestone.substantiation'), value: 'comment' }
      ],
      file: null,
      importedMilestones: [],
      selectedMilestones: [],
      selectedSubMilestones: {},
      excelLabels: {
        orderNumber: EXCEL_LABELS.ORDER_NUMBER,
        originalDate: EXCEL_LABELS.ORIGINAL_MILESTONE_DATE,
        forecastedDate: EXCEL_LABELS.FORECASTED_MILESTONE_DATE,
        expectedInvoiceDate: EXCEL_LABELS.EXPECTED_INVOICE_DATE,
        expectedPaymentDate: EXCEL_LABELS.EXPECTED_PAYMENT_DATE,
        milestoneName: EXCEL_LABELS.MILESTONE_NAME,
        subMilestoneName: EXCEL_LABELS.SUB_MILESTONE_NAME,
        amount: EXCEL_LABELS.AMOUNT,
        substantiation: EXCEL_LABELS.SUBSTANTIATION
      }
    }
  },
  computed: {
    selectAll () {
      return !!(this.selectedMilestones.length &&
        this.selectedMilestones.length === this.milestonesWithError.filter(i => !i.hasError).length)
    },
    selectAllIndeterminate () {
      return !!(this.selectedMilestones.length !== this.milestonesWithError.filter(i => !i.hasError).length &&
        this.selectedMilestones.length)
    },
    warningMessages () {
      const warnings = this.importedMilestones.map(item => {
        const itemCheck = this.checkForNameDuplicate(item)
        if (itemCheck.error) {
          return itemCheck.warning ? [itemCheck.warning] : []
        }
        return [
          ...item.subMilestones.map(subMilestone => this.checkForNameDuplicate(subMilestone).warning),
          itemCheck.warning
        ]
      }).flat().filter(i => i && i.length)
      return warnings
    },
    errorMessage () {
      const errors = []
      if (this.sumOfSelectedMilestonesHigherThanContractPrice) {
        errors.push(this.$t('payment.milestone.selectedMilestoneSumError'))
      }
      errors.push(...this.importedMilestones.map(item => {
        const errors = Object.keys(item || {}).map(key => {
          return errorMessageGenerator(this.$v.importedMilestones.$each[item.index][key], key)
        })
        const nameDuplicate = this.checkForNameDuplicate(item).error
        const subErrors = nameDuplicate
          ? []
          : item.subMilestones.map(subMilestone => {
            return [
              ...Object.keys(subMilestone || {}).map(key => {
                return errorMessageGenerator(this.$v.importedMilestones.$each[item.index].subMilestones.$each[subMilestone.index][key], key)
              }),
              this.checkForNameDuplicate(subMilestone).error
            ]
          })
        return [
          ...errors,
          ...subErrors,
          nameDuplicate
        ]
      }).flat(5).filter(i => i && i.length))
      return [...new Set(errors)]
    },
    sumOfSelectedMilestonesHigherThanContractPrice () {
      const sumOfMilestones = [
        ...this.milestones.filter(i => !i.variationId && !i.liquidatedDamageId),
        ...this.selectedMilestones.filter(i => !i.existingMilestone.id)
      ].reduce((acc, curr) => {
        if (curr.existingMilestone && curr.existingMilestone.id) {
          acc -= curr.existingMilestone.paymentAmountCents
        }
        return acc + curr.paymentAmountCents
      }, 0)
      return sumOfMilestones > this.contract.signedPriceCents
    },
    notAllSelectedWarning () {
      return this.selectedMilestones.length !== this.importedMilestones.length
        ? this.$t('payment.milestone.notAllSelected')
        : null
    },
    milestonesWithError () {
      return this.importedMilestones.map(i => {
        const duplicate = this.checkForNameDuplicate(i)
        const error = Object.entries(this.$v.importedMilestones.$each[i.index])
          .reduce((acc, [key, value]) => (!key.startsWith('$') && key !== 'subMilestones') ? (acc || value.$error) : acc, false)
        return {
          ...i,
          hasError: error || !!duplicate.error,
          hasWarning: !!duplicate.warning,
          subMilestones: i.subMilestones.map(j => {
            const duplicateSubMilestone = this.checkForNameDuplicate(j)
            return {
              ...j,
              hasError: this.$v.importedMilestones.$each[i.index].subMilestones.$each[j.index].$error || !!duplicateSubMilestone.error || !!duplicate.error,
              hasWarning: duplicate.error ? false : !!duplicateSubMilestone.warning
            }
          })
        }
      })
    },
    partTranslationKey () {
      return getLocalizationContext(this.contract.isMultiCurrency)
    }
  },
  methods: {
    handleSelectSubMilestone (milestone, { item, value }) {
      if (value) {
        !this.selectedMilestones.find(i => i.index === milestone.index)
          ? this.selectedMilestones.push(milestone)
          : this.selectedMilestones = [...this.selectedMilestones]
        this.selectedSubMilestones[milestone.index]
          ? this.selectedSubMilestones[milestone.index].push(item)
          : this.selectedSubMilestones[milestone.index] = [item]
      } else {
        this.selectedSubMilestones[milestone.index].splice(this.selectedSubMilestones[milestone.index].findIndex(i => i.index === item.index), 1)
        if (this.selectedSubMilestones[milestone.index].length === 0) {
          this.selectedMilestones.splice(this.selectedMilestones.findIndex(i => i.index === milestone.index), 1)
        }
      }
    },
    handleSelectMilestone (value, item) {
      if (value) {
        this.selectedMilestones.push(item)
        this.selectedSubMilestones[item.index] = item.subMilestones.filter(i => !i.hasError)
      } else {
        this.selectedMilestones.splice(this.selectedMilestones.findIndex(i => i.index === item.index), 1)
        this.selectedSubMilestones[item.index] = []
      }
    },
    handleSelectAll (value) {
      this.selectedMilestones = value ? this.milestonesWithError.filter(i => !i.hasError) : []
      this.selectedSubMilestones = value
        ? this.selectedMilestones.reduce((acc, curr) => ({ ...acc, [curr.index]: curr.subMilestones.filter(i => !i.hasError) }), {})
        : {}
    },
    checkForNameDuplicate (item) {
      const hasDuplicate = checkMilestonesNamesDuplicates(item, [...this.milestones, ...this.importedMilestones], 2)
      const existingMilestone = item.existingMilestone
      if (hasDuplicate) {
        if (existingMilestone.id) {
          if (existingMilestone.parentMilestoneId === item.parentMilestoneId) {
            return existingMilestone.amountAssignedToApplication.sum
              ? { error: this.$t('payment.milestone.paymentApplicationExist', { name: item.name }) }
              : { warning: this.$t('payment.milestone.duplicatedMayCauseError', { name: item.name }) }
          }
          return existingMilestone.amountAssignedToApplication.sum
            ? { error: this.$t('payment.milestone.paymentApplicationExist', { name: item.name }) }
            : { warning: this.$t('payment.milestone.duplicatedMayCauseError', { name: item.name }) }
        }
        return { error: this.$t('payment.milestone.duplicatedMayCauseError', { name: item.name }) }
      }
      return {}
    },
    handleFileChange (v) {
      if (v) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const data = e.target.result
          const excel = XLSX.read(data, { type: 'binary' })
          const sheet = excel.Sheets.Sheet1 || excel.Sheets[excel.SheetNames[0]]
          this.importedMilestones = XLSX.utils.sheet_to_json(sheet, { raw: false })
            .reduce((acc, curr) => {
              const part = this.contract.parts.find(i => i.purchaseOrder === curr[this.excelLabels.orderNumber]

              ) || { name: this.$t(this.partTranslationKey + '.all') }

              const amount = curr.Amount || curr.Payment
              const item = {
                paymentAmountCents: amount ? Number(String(amount).replace(',', '.')) * 100 : null,
                percentOfContractPrice: amount && Number(String(amount).replace(',', '.')) * 10000 / this.contract.signedPriceCents,
                description: curr.Description,
                purchaseOrder: curr[this.excelLabels.orderNumber],
                part,
                originalDate: dayjs(curr[this.excelLabels.originalDate], ACCEPTED_EXCEL_IMPORT_DATE_FORMATS).format(ISO_DATE_FORMAT),
                forecastedDate: dayjs(curr[this.excelLabels.forecastedDate], ACCEPTED_EXCEL_IMPORT_DATE_FORMATS).format(ISO_DATE_FORMAT),
                expectedInvoiceDate: dayjs(curr[this.excelLabels.expectedInvoiceDate], ACCEPTED_EXCEL_IMPORT_DATE_FORMATS).format(ISO_DATE_FORMAT),
                expectedPaymentDate: dayjs(curr[this.excelLabels.expectedPaymentDate], ACCEPTED_EXCEL_IMPORT_DATE_FORMATS).format(ISO_DATE_FORMAT),
                comment: curr.Substantiation,
                partId: part.id
              }
              if (curr[this.excelLabels.milestoneName]) {
                const existingMilestone = this.milestones.find(i => i.name === curr[this.excelLabels.milestoneName])
                acc.push({
                  ...item,
                  multiCurrencyMilestone: !item.paymentAmountCents,
                  name: curr[this.excelLabels.milestoneName],
                  subMilestones: [],
                  index: acc.length,
                  existingMilestone: existingMilestone || {}
                })
              } else if (acc[acc.length - 1]) {
                const parentSubMilestones = acc[acc.length - 1].existingMilestone.subMilestones
                const existingMilestone = parentSubMilestones
                  ? parentSubMilestones.find(i => i.name === curr[this.excelLabels.subMilestoneName])
                  : {}
                acc[acc.length - 1].subMilestones.push({
                  ...item,
                  name: curr[this.excelLabels.subMilestoneName],
                  percentOfMilestone: amount && Number(String(amount).replace(',', '.')) * 10000 / acc[acc.length - 1].paymentAmountCents,
                  index: acc[acc.length - 1].subMilestones.length,
                  existingMilestone: existingMilestone || {},
                  parentMilestoneId: acc[acc.length - 1].existingMilestone.id
                })
              }
              return acc
            }, [])
          this.$v.$touch()
        }
        reader.readAsBinaryString(v)
        reader.onerror = function (ex) { }
      }
    },
    async handleImportMilestones () {
      if (!this.sumOfSelectedMilestonesHigherThanContractPrice) {
        await Promise.all(this.selectedMilestones.map(async item => {
          let milestone
          if (item.existingMilestone.id) {
            item.id = item.existingMilestone.id
            item.contractId = item.existingMilestone.contractId
            milestone = await updatePaymentMilestone(item)
          } else {
            milestone = await addPaymentMilestone(this.$route.params.contractId, item)
          }
          const subMilestones = this.selectedSubMilestones[item.index].map(i => ({ ...i, id: i.existingMilestone.id || null }))
          await saveSubMilestones(this.$route.params.contractId, milestone, subMilestones)
        }))
        this.$emit('FETCH_MILESTONES')
        this.handleClose()
      }
    },
    handleClose () {
      this.$eventBus.$emit('CLOSE_MODAL')
      this.file = null
      this.importedMilestones = []
    },
    importedMilestonesClasses (item) {
      return item.hasError ? 'error-row' : item.hasWarning ? 'warn-row' : ''
    }
  },
  watch: {
    selectedMilestones (v) {
      if (v.some(i => i.hasError)) {
        this.selectedMilestones = this.selectedMilestones.filter(i => !i.hasError)
      }
    },
    milestonesWithError (v) {
      this.selectedMilestones = v.filter(i => !i.hasError)
      this.selectedSubMilestones = this.selectedMilestones.reduce((acc, curr) => {
        acc[curr.index] = curr.subMilestones.filter(i => !i.hasError)
        return acc
      }, {})
    }
  },
  validations: milestonesImportModel
}
</script>

<style>
.error-row {
  background-color:rgba(202, 56, 56, 0.5);
}
.warn-row {
  background-color:rgba(209, 127, 32, 0.5);
}
</style>
